import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FEATURE_TEXT = {
  ai_credits: (v) => `${v.toLocaleString()} AI credits / month`, autopilot: (v) => (v ? "Career Autopilot recommendations" : "Autopilot not included"),
  imported_links: (v) => `${v.toLocaleString()} saved job links`, premium_model: (v) => (v ? "Premium model on every agent" : "Smart model routing"),
  job_posts: (v) => `${v} active job posts`, candidate_contacts: (v) => `${v} candidate contact requests / mo`, seats: (v) => `${v} seat${v > 1 ? "s" : ""}`,
};

export const PlanCard = ({ plan, current, onSelect, busy, paidActive }) => (
  <div data-testid={`plan-card-${plan.plan_id}`} className={`lift flex flex-col rounded-2xl border bg-white p-7 ${plan.plan_id === "cand_careerpro" || plan.plan_id === "rec_growth" ? "ring-2 ring-primary" : ""}`}>
    <h3 className="font-display text-lg font-bold">{plan.name}</h3>
    <p className="mt-3 font-display text-4xl font-extrabold">{plan.price == null ? "Custom" : `$${plan.price % 1 ? plan.price.toFixed(2) : plan.price}`}<span className="text-sm font-medium text-slate-500">{plan.price ? " /month" : ""}</span></p>
    <ul className="mt-6 flex-1 space-y-2 text-sm">
      {Object.entries(plan.entitlements).filter(([k]) => FEATURE_TEXT[k]).map(([k, v]) => (
        <li key={k} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{FEATURE_TEXT[k](v)}</li>
      ))}
    </ul>
    <Button className="mt-7 rounded-full" variant={current ? "outline" : "default"} disabled={current || busy || (paidActive && plan.price === 0)} data-testid={`plan-select-${plan.plan_id}`} onClick={() => onSelect(plan)}>
      {current ? "Current plan" : plan.price == null ? "Contact sales" : plan.price === 0 ? (paidActive ? "Cancel to downgrade" : "Start free") : paidActive ? "Switch plan" : "Subscribe"}
    </Button>
  </div>
);

export function usePlanSelect() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const select = async (plan) => {
    if (plan.price == null) { window.location.href = "mailto:contactus@jobpedestal.com?subject=Enterprise%20plan"; return; }
    if (!user) { nav(`/register?role=${plan.audience}`); return; }
    if (!plan.lookup_key) { toast.info("Free plans are active by default"); return; }
    setBusy(true);
    const body = { plan_id: plan.plan_id, origin_url: window.location.origin };
    try {
      const { data } = await api.post("/payments/checkout", body);
      window.location.href = data.checkout_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "plan_change_requires_confirmation") {
        if (window.confirm(d.message)) {
          try { await api.post("/payments/checkout", { ...body, confirm_change: true }); toast.success(`Switched to ${plan.name}`); await refresh(); }
          catch (e2) { toast.error(errMsg(e2)); }
        }
      } else { toast.error(errMsg(e)); }
      setBusy(false);
    }
  };
  return { select, busy, user };
}

export function PlanGrid({ audience }) {
  const [plans, setPlans] = useState([]);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const { select, busy, user } = usePlanSelect();
  useEffect(() => {
    let alive = true;
    let timer;
    const load = (n) => api.get("/plans").then((r) => { if (alive) { setPlans(r.data); setFailed(false); } })
      .catch(() => { if (!alive) return; if (n < 4) { timer = setTimeout(() => load(n + 1), 1500 * 2 ** n); } else { setFailed(true); } });
    setFailed(false);
    load(0);
    return () => { alive = false; clearTimeout(timer); };
  }, [attempt]);
  const list = plans.filter((p) => p.audience === audience && p.plan_id !== "rec_free");
  if (!plans.length) {
    return failed ? (
      <div className="rounded-xl border p-6 text-sm" data-testid="plans-error">
        Pricing couldn't load right now. <button className="font-medium text-primary underline" onClick={() => setAttempt((a) => a + 1)} data-testid="plans-retry-button">Try again</button>
      </div>
    ) : <p className="text-sm text-muted-foreground" data-testid="plans-loading">Loading plans…</p>;
  }
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {list.map((p) => <PlanCard key={p.plan_id} plan={p} busy={busy} current={user && user.plan === p.plan_id} paidActive={["active", "trialing", "past_due"].includes(user?.subscription?.status)} onSelect={select} />)}
    </div>
  );
}
